import { useEffect, useMemo } from 'react';

/**
 * Enhanced image preloading hook that supports different image sources
 * @param {Array} images - Array of image objects or URLs
 * @param {Object} options - Configuration options
 * @param {string} options.urlProperty - Property name to extract URL from image objects (default: 'url')
 * @param {boolean} options.enabled - Whether preloading is enabled (default: true)
 * @param {number} options.priority - Priority level (higher numbers load first) (default: 0)
 */
export function usePreloadImages(images = [], options = {}) {
  const {
    urlProperty = 'url',
    enabled = true
  } = options;

  // Memoize URL extraction to avoid recalculating on every render
  const urls = useMemo(() => {
    if (!Array.isArray(images) || !enabled) return [];
    
    return images
      .map(image => {
        if (typeof image === 'string') return image;
        if (typeof image === 'object' && image !== null) {
          return image[urlProperty] || image.image_url || image.src;
        }
        return null;
      })
      .filter(url => url && typeof url === 'string');
  }, [images, urlProperty, enabled]);

  useEffect(() => {
    if (!enabled || urls.length === 0) return;

    // Create a cleanup function to handle component unmounting
    const imageElements = [];

    const preloadImage = (url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          resolve(img);
        };
        
        img.onerror = () => {
          console.warn(`Failed to preload image: ${url}`);
          reject(new Error(`Failed to load ${url}`));
        };
        
        img.src = url;
        imageElements.push(img);
      });
    };

    // Sort URLs by priority if needed (for future enhancement)
    const sortedUrls = [...urls];
    
    // Preload images with batching to avoid overwhelming the browser
    const batchSize = 5;
    const preloadBatch = async (startIndex = 0) => {
      const batch = sortedUrls.slice(startIndex, startIndex + batchSize);
      if (batch.length === 0) return;

      try {
        await Promise.allSettled(batch.map(preloadImage));
      } catch (error) {
        console.warn('Batch preloading error:', error);
      }

      // Continue with next batch after a small delay
      if (startIndex + batchSize < sortedUrls.length) {
        setTimeout(() => preloadBatch(startIndex + batchSize), 100);
      }
    };

    preloadBatch();

    // Cleanup function
    return () => {
      imageElements.forEach(img => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [urls, enabled]);

  return {
    preloadedCount: urls.length,
    isEnabled: enabled
  };
}

/**
 * Hook for preloading images from data arrays (accommodations, attractions, etc.)
 * @param {Array} dataArray - Array of data objects containing images
 * @param {Object} options - Configuration options
 */
export function usePreloadDataImages(dataArray = [], options = {}) {
  const {
    imageProperty = 'image_url',
    additionalProperties = [],
    enabled = true
  } = options;

  const images = useMemo(() => {
    if (!Array.isArray(dataArray)) return [];
    
    const allImages = [];
    
    dataArray.forEach(item => {
      // Main image property
      if (item[imageProperty]) {
        allImages.push(item[imageProperty]);
      }
      
      // Additional image properties
      additionalProperties.forEach(prop => {
        if (item[prop]) {
          if (Array.isArray(item[prop])) {
            allImages.push(...item[prop]);
          } else {
            allImages.push(item[prop]);
          }
        }
      });
    });
    
    return allImages;
  }, [dataArray, imageProperty, additionalProperties]);

  return usePreloadImages(images, { enabled, urlProperty: null });
}
